import { STORE_LOGIN_PATHNAME, STORE_REDIRECT_QUERY_PARAMS } from './useStoreRedirect.consts';
import {
    EStoreRedirectEventType,
    IStoreRedirectDeps,
    IStoreRedirectEventData,
    IStoreRedirectOptions,
} from './useStoreRedirect.types';

const getOrigin = (): string | undefined =>
    typeof window === 'undefined' ? undefined : window.location.origin;

/** Prevents concurrent redirects from racing (e.g. nested CTA click handlers). */
let isRedirectInFlight = false;

/** Test-only: clear the in-flight lock between cases. */
export const resetStoreRedirectInFlightForTests = (): void => {
    isRedirectInFlight = false;
};

export const buildLoginRedirectUrl = (
    redirectUrl: string,
    token: string,
    autoCollectOfferId?: string,
    journeyId?: string,
): string => {
    const storeUrl = new URL(redirectUrl, getOrigin());
    storeUrl.pathname = STORE_LOGIN_PATHNAME;
    storeUrl.searchParams.set(STORE_REDIRECT_QUERY_PARAMS.REDIRECT_LOGIN_TOKEN, token);
    if (journeyId) {
        storeUrl.searchParams.set(STORE_REDIRECT_QUERY_PARAMS.JOURNEY_ID, journeyId);
    }

    if (autoCollectOfferId) {
        storeUrl.searchParams.set(STORE_REDIRECT_QUERY_PARAMS.AUTO_COLLECT, 'true');
        storeUrl.searchParams.set(STORE_REDIRECT_QUERY_PARAMS.POPUP_ID, autoCollectOfferId);
    }

    return storeUrl.toString();
};

export const navigate = (url: string): void => {
    if (typeof window === 'undefined') return;
    window.location.assign(url);
};

export const performStoreRedirect = async (
    deps: IStoreRedirectDeps,
    redirectUrl?: string,
    isStoreRedirect?: boolean,
    options?: IStoreRedirectOptions,
): Promise<void> => {
    if (!redirectUrl) return;
    // Nested click handlers (CTA image + parent) can fire twice in one gesture.
    // Without this lock, a later failure can location.assign the bare URL and
    // overwrite a successful token navigation — especially visible in Firefox.
    if (isRedirectInFlight) return;
    isRedirectInFlight = true;

    try {
        if (!isStoreRedirect || !deps.isLoggedIn) {
            navigate(redirectUrl);
            return;
        }

        const createJourneyId = deps.createJourneyId || (() => crypto.randomUUID());
        const now = deps.now || Date.now;
        const journeyId = createJourneyId();
        const startedAtMilliseconds = now();
        const destinationHost = new URL(redirectUrl, getOrigin()).host;
        const baseEventData = { destinationHost, journeyId };
        emitRedirectEvent(deps, EStoreRedirectEventType.STARTED, baseEventData);

        try {
            const token = await deps.getRedirectToken();
            const storeUrl = buildLoginRedirectUrl(
                redirectUrl,
                token,
                options?.autoCollectOfferId,
                journeyId,
            );
            emitRedirectEvent(deps, EStoreRedirectEventType.TOKEN_CREATED, {
                ...baseEventData,
                durationMilliseconds: now() - startedAtMilliseconds,
            });

            if (options?.autoCollectOfferId) {
                deps.setAutoCollectFlag();
            }

            emitRedirectEvent(deps, EStoreRedirectEventType.NAVIGATION_DISPATCHED, {
                ...baseEventData,
                durationMilliseconds: now() - startedAtMilliseconds,
            });
            navigate(storeUrl);
        } catch (error) {
            console.error('[useStoreRedirect] failed to redirect to store with login token', error);
            emitRedirectEvent(deps, EStoreRedirectEventType.FAILED, {
                ...baseEventData,
                durationMilliseconds: now() - startedAtMilliseconds,
                reasonCode: 'token-mint-failed',
            });
            navigate(redirectUrl);
        }
    } finally {
        isRedirectInFlight = false;
    }
};

const emitRedirectEvent = (
    deps: IStoreRedirectDeps,
    eventType: EStoreRedirectEventType,
    eventData: IStoreRedirectEventData,
): void => {
    deps.onRedirectEvent?.([eventType, eventData]);
};
